import React, { useCallback, useRef, useState } from "react";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Button, Stack, Typography } from "@mui/material";
import { useModals, useQueryHook } from "hooks";
import { useUserManageActions } from "store";
import { ICharContact } from "store/types";

import { AddContactPopup } from "./AddContactPopup";
import { ContactCard } from "./ContactCard";
import { ContactFormDialog } from "./ContactFormDialog";

interface Props {
  contacts: ICharContact[];
  // Update the contacts array. Receives an updater so callers can splice it into their own state slice
  // (deal characteristics `state.contact`, deal-create `state.contacts`, …).
  onChange: (updater: (prev: ICharContact[]) => ICharContact[]) => void;
  viewOnly?: boolean;
  // Server validation errors keyed by contact id — highlights the matching cards.
  errors?: Record<string, string>;
}

// Which dialog is open: the account-contact picker, or the create/edit form (create can be reached
// from the picker, so it remembers to return there on "Back").
type DialogState =
  | { kind: "picker" }
  | { kind: "create"; fromPicker: boolean }
  | { kind: "edit"; contact: ICharContact }
  | null;

const contactLabel = (contact: ICharContact): string =>
  [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim() || "this contact";

// Reusable contact editor: compact cards + Role dropdown, an "Add Contact" picker (existing account
// contacts) and a create/edit form. Used by the deal characteristics drawer and deal creation.
export const ContactList: React.FC<Props> = ({ contacts, onChange, viewOnly = false, errors }) => {
  const { confirmModal } = useModals();
  const { getContactDealRolesAction } = useUserManageActions();

  // Per-deal role options (key/label) for the Role dropdown — sourced from GET /contacts/deal-roles.
  const { data: dealRoles = [] } = useQueryHook(getContactDealRolesAction);

  const [dialog, setDialog] = useState<DialogState>(null);
  // The "Add Contact" button — anchors the picker and the create/edit form (both Popovers, no backdrop).
  const addBtnRef = useRef<HTMLButtonElement>(null);

  const changeRole = useCallback(
    (id: number | string, role: string) =>
      onChange((prev) => prev.map((contact) => (contact.id === id ? { ...contact, role } : contact))),
    [onChange]
  );

  // Append a newly-created contact.
  const addToDeal = useCallback(
    (contact: ICharContact) => onChange((prev) => [...prev, contact]),
    [onChange]
  );

  // Replace an edited contact in place (matched by id).
  const updateInPlace = useCallback(
    (updated: ICharContact) =>
      onChange((prev) => prev.map((contact) => (contact.id === updated.id ? updated : contact))),
    [onChange]
  );

  // Apply from the picker — attach the chosen account contacts, skipping any already added.
  const handleApplySelected = useCallback(
    (selected: ICharContact[]) =>
      onChange((prev) => {
        const existing = new Set(prev.map((contact) => contact.id));
        const toAdd = selected.filter((contact) => contact.id == null || !existing.has(contact.id));
        return [...prev, ...toAdd];
      }),
    [onChange]
  );

  // Remove: an existing contact (numeric id) is soft-deleted — kept in state and submitted with
  // is_deleted: true; a new/unsaved contact (uuid) is just dropped. Either way it leaves the list.
  const handleRemove = useCallback(
    (contact: ICharContact) => {
      confirmModal({
        msg: `Are you sure you want to remove the contact "${contactLabel(contact)}" from the deal?`,
        title: "Remove Contact",
        action: () =>
          onChange((prev) =>
            typeof contact.id === "number"
              ? prev.map((item) => (item.id === contact.id ? { ...item, is_deleted: true } : item))
              : prev.filter((item) => item.id !== contact.id)
          )
      });
    },
    [confirmModal, onChange]
  );

  return (
    <Stack sx={{ gap: "16px" }}>
      {/* Soft-deleted contacts stay in state (submitted with is_deleted: true) but are hidden from the list. */}
      {contacts
        .filter((contact) => !contact.is_deleted)
        .map((contact) => (
          <ContactCard
            key={contact.id}
            contact={contact}
            roleOptions={dealRoles}
            disabled={viewOnly}
            error={contact.id != null ? errors?.[String(contact.id)] : undefined}
            onChangeRole={changeRole}
            onEdit={(target) => setDialog({ kind: "edit", contact: target })}
            onRemove={handleRemove}
          />
        ))}

      {!viewOnly && (
        <Stack direction="row" justifyContent="flex-end">
          <Button
            ref={addBtnRef}
            variant="text"
            sx={{ background: "#f0f0f0", color: "#071F35", padding: "4px 10px", gap: "4px" }}
            onClick={() => setDialog({ kind: "picker" })}
          >
            <AddCircleOutlineIcon sx={{ fontSize: "16px" }} />
            <Typography fontSize="12px" sx={{ color: "#071F35" }}>
              Add Contact
            </Typography>
          </Button>
        </Stack>
      )}

      <AddContactPopup
        open={dialog?.kind === "picker"}
        anchorEl={addBtnRef.current}
        onClose={() => setDialog(null)}
        existingContactIds={contacts.map((contact) => contact.id).filter((id): id is number | string => id != null)}
        onApply={handleApplySelected}
        onCreateNew={() => setDialog({ kind: "create", fromPicker: true })}
      />

      <ContactFormDialog
        open={dialog?.kind === "create" || dialog?.kind === "edit"}
        mode={dialog?.kind === "edit" ? "edit" : "create"}
        initial={dialog?.kind === "edit" ? dialog.contact : null}
        anchorEl={addBtnRef.current}
        onClose={() => setDialog(null)}
        onBack={dialog?.kind === "create" && dialog.fromPicker ? () => setDialog({ kind: "picker" }) : undefined}
        onSubmit={dialog?.kind === "edit" ? updateInPlace : addToDeal}
      />
    </Stack>
  );
};
